"use client"

import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useTranslation } from "@/hooks/use-translation"
import { formatNumber } from "@/lib/locale"

export const PAGE_SIZES = [10, 25, 50, 100] as const
export const DEFAULT_PAGE_SIZE = 25

interface Props {
  totalCount: number
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
}

/** Shared table footer: "Showing x–y of z", page-size choice, previous/next. */
export function TablePager({ totalCount, page, pageSize, onPageChange, onPageSizeChange }: Props) {
  const { t, language } = useTranslation()
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize))
  const clampedPage = Math.min(page, pageCount)
  const first = totalCount === 0 ? 0 : (clampedPage - 1) * pageSize + 1
  const last = Math.min(clampedPage * pageSize, totalCount)

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 px-1 py-2 text-sm">
      <span className="text-muted-foreground">
        {t("pager.showing", {
          first: formatNumber(first, language),
          last: formatNumber(last, language),
          total: formatNumber(totalCount, language),
        })}
      </span>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1">
          <span className="text-xs text-muted-foreground">{t("pager.perPage")}</span>
          <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
            <SelectTrigger className="h-8 w-[76px]" aria-label={t("pager.rowsPerPageAria")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            aria-label={t("pager.previousPage")}
            disabled={clampedPage <= 1}
            onClick={() => onPageChange(clampedPage - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[90px] text-center text-xs text-muted-foreground">
            {t("pager.pageOf", {
              page: formatNumber(clampedPage, language),
              pageCount: formatNumber(pageCount, language),
            })}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            aria-label={t("pager.nextPage")}
            disabled={clampedPage >= pageCount}
            onClick={() => onPageChange(clampedPage + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

/** Clamp-and-slice helper so every table pages the same way. */
export function pageSlice<T>(rows: T[], page: number, pageSize: number): T[] {
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize))
  const clampedPage = Math.min(page, pageCount)
  return rows.slice((clampedPage - 1) * pageSize, clampedPage * pageSize)
}
